import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { ExternalLink, HelpCircle, TrendingUp } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { ContributionPeriodOptions } from "@shared-core/data/adapter";
import type * as API from "@shared-core/data/api-types";
import {
  formatCurrencyGbp,
  formatCurrencyGbpCompact,
} from "@shared-core/domain/formatters";
import { buildContributionBurnUp } from "@shared-core/domain/contributionBurnUp";
import { isRouteEnabled, paths } from "@shared-core/routing/routes";
import { Card } from "../../primitives/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../primitives/tooltip";
import { ContributionBurnUpChart } from "./ContributionBurnUpChart";
import { currentTime } from "@shared-core/domain/current-time";

type PaceStatus = "exceeding" | "on_pace" | "slightly_behind" | "behind";

// dayIndex/totalDays are 1-based positions within the viewed period (a
// calendar month or a fiscal quarter) — period-agnostic, so the same ratio
// classifies pace for either span.
function getPaceStatus(
  actual: number,
  target: number,
  dayIndex: number,
  totalDays: number,
): PaceStatus {
  if (target <= 0) return "exceeding";
  if (actual >= target) return "exceeding";

  const expectedPace = totalDays > 0 ? target * (dayIndex / totalDays) : 0;
  if (actual >= expectedPace) return "on_pace";
  if (actual >= expectedPace * 0.9) return "slightly_behind";
  return "behind";
}

type PeriodPreset =
  "this_month" | "last_month" | "this_quarter" | "last_quarter" | "custom";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function shiftMonth(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const idx = year * 12 + (month - 1) + delta;
  return { year: Math.floor(idx / 12), month: (idx % 12) + 1 };
}

function periodOptionsForPreset(
  preset: PeriodPreset,
  now: Date,
  custom: { year: number; month: number },
): ContributionPeriodOptions {
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  switch (preset) {
    case "last_month": {
      const p = shiftMonth(year, month, -1);
      return { year: p.year, month: p.month, span: "month" };
    }
    case "this_quarter":
      return { year, month, span: "quarter" };
    case "last_quarter": {
      const p = shiftMonth(year, month, -3);
      return { year: p.year, month: p.month, span: "quarter" };
    }
    case "custom":
      return { year: custom.year, month: custom.month, span: "month" };
    case "this_month":
    default:
      return { year, month, span: "month" };
  }
}

// Reads GET /views/dashboard/monthly-contribution: the booked contribution
// vs target for a user-selected period (this/last month, this/last fiscal
// quarter, or a custom past month — backlog #22), the real per-day
// cumulative series (for the burn-up), and comparisons against the
// immediately preceding period and the same period a year ago. When no
// target is published the card shows an honest empty state — no pace line,
// no illustrative number.
export function ContributionTargetCard() {
  const dataAdapter = useDataAdapter();
  const [contribution, setContribution] =
    useState<API.MonthlyContribution | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preset, setPreset] = useState<PeriodPreset>("this_month");
  const now = useMemo(() => currentTime(), []);
  const [customPeriod, setCustomPeriod] = useState({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  });

  useEffect(() => {
    let cancelled = false;
    // Keep the previous period's figures on screen while the next period
    // loads (stale-while-revalidate) instead of flashing to empty — a
    // failed fetch surfaces as a visible error, never as a silent £0.
    setError(null);
    dataAdapter
      .getMonthlyContribution(periodOptionsForPreset(preset, now, customPeriod))
      .then((result) => {
        if (cancelled) return;
        setContribution(result);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, preset, now, customPeriod]);

  const actual = contribution?.actual ?? 0;
  const target = contribution?.target ?? 0;
  const hasRealTarget = target > 0;
  const isCurrentPeriod = contribution?.is_current_period ?? true;

  const periodLabel = contribution
    ? contribution.span === "quarter"
      ? `Q${contribution.quarter_number} FY${String(contribution.fiscal_year).slice(-2)}`
      : `${MONTH_NAMES[contribution.month - 1]} ${contribution.year}`
    : "";

  const burnUp = contribution ? buildContributionBurnUp(contribution) : null;

  const status =
    hasRealTarget && burnUp
      ? getPaceStatus(actual, target, burnUp.today, burnUp.daysInMonth)
      : "exceeding";
  const isExceeding = status === "exceeding";
  const isAheadOfPace = status === "on_pace";
  const expectedPace =
    hasRealTarget && burnUp
      ? target * (burnUp.today / Math.max(burnUp.daysInMonth, 1))
      : 0;
  const paceGap = actual - expectedPace;
  const daysRemaining = burnUp ? burnUp.daysInMonth - burnUp.today : 0;

  const comparisons: Array<{ label: string; value: number }> = contribution
    ? [
        { label: "vs prior period", value: contribution.prior_period_actual },
        {
          label: "vs same period last year",
          value: contribution.year_ago_period_actual,
        },
      ]
    : [];

  return (
    <Card className="p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-1.5">
          {/* The card is sales-home content; its links lead into `planning`,
              which is held back. Keep the heading, drop the link. */}
          {isRouteEnabled("targets") ? (
            <Link
              to={paths.targets()}
              className="text-sm text-muted-foreground flex items-center gap-2 hover:text-foreground transition-colors"
            >
              <TrendingUp className="size-4" />
              Booked Contribution
            </Link>
          ) : (
            <span className="text-sm text-muted-foreground flex items-center gap-2">
              <TrendingUp className="size-4" />
              Booked Contribution
            </span>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="size-3.5 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-64">
              Contribution (price minus direct cost) on orders already booked in
              — not profit, and not yet invoiced. Revenue below is the same
              orders' full sale price, before cost is deducted.
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="flex items-center gap-3">
          {isRouteEnabled("targets") && (
            <Link
              to={paths.targets()}
              className="text-xs text-primary inline-flex items-center gap-1 hover:underline"
            >
              View all my targets
              <ExternalLink className="size-3" />
            </Link>
          )}
          <Select
            value={preset}
            onValueChange={(v) => setPreset(v as PeriodPreset)}
          >
            <SelectTrigger className="h-7 text-xs w-[150px]" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">This month</SelectItem>
              <SelectItem value="last_month">Last month</SelectItem>
              <SelectItem value="this_quarter">This quarter</SelectItem>
              <SelectItem value="last_quarter">Last quarter</SelectItem>
              <SelectItem value="custom">Choose a month…</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {preset === "custom" && (
        <div className="flex items-center gap-2 mb-4">
          <Select
            value={String(customPeriod.month)}
            onValueChange={(v) =>
              setCustomPeriod((p) => ({ ...p, month: Number(v) }))
            }
          >
            <SelectTrigger className="h-7 text-xs w-[110px]" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTH_NAMES.map((name, i) => (
                <SelectItem key={name} value={String(i + 1)}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={String(customPeriod.year)}
            onValueChange={(v) =>
              setCustomPeriod((p) => ({ ...p, year: Number(v) }))
            }
          >
            <SelectTrigger className="h-7 text-xs w-[90px]" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map(
                (yr) => (
                  <SelectItem key={yr} value={String(yr)}>
                    {yr}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {error && contribution && (
        <div className="text-xs text-red-700 mb-3">
          Couldn't refresh contribution — showing the last loaded figures.
        </div>
      )}

      {/* Compact layout: KPIs and comparisons on the left, the burn-up chart
          beside them, so the card stays short and the to-do list and
          "where things stand" below stay in view without scrolling. */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="sm:w-[58%] shrink-0">
          {/* Two KPIs in a row — dominant headline left, quiet secondary right */}
          <div className="flex items-baseline justify-between gap-4 mb-2">
            <div>
              <div className="text-3xl font-bold text-foreground tracking-tight">
                {formatCurrencyGbp(actual)}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {hasRealTarget
                  ? `of ${formatCurrencyGbpCompact(target)} ${periodLabel} target`
                  : `booked in ${periodLabel}`}
              </div>
              {contribution && (
                <div className="text-xs text-muted-foreground mt-1">
                  {formatCurrencyGbpCompact(contribution.revenue_actual)}{" "}
                  revenue
                </div>
              )}
            </div>
            <div className="text-right">
              {hasRealTarget ? (
                <>
                  <div className="text-xl font-semibold text-muted-foreground tabular-nums">
                    {formatCurrencyGbp(
                      isExceeding ? actual - target : Math.abs(paceGap),
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {isExceeding
                      ? "over target"
                      : isCurrentPeriod
                        ? `${isAheadOfPace ? "ahead of" : "behind"} the pace, ${daysRemaining}d left`
                        : "under target"}
                  </div>
                </>
              ) : (
                <div className="text-xs text-muted-foreground mt-1 max-w-[9rem]">
                  No {periodLabel} target set
                </div>
              )}
            </div>
          </div>

          {contribution && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {comparisons.map((c) => (
                <span key={c.label}>
                  {c.label}:{" "}
                  <span className="font-medium text-foreground tabular-nums">
                    {formatCurrencyGbpCompact(c.value)}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {burnUp ? (
            <ContributionBurnUpChart
              burnUp={burnUp}
              targetGbp={target}
              actualGbp={actual}
            />
          ) : error ? (
            <div className="flex h-28 items-center justify-center text-sm text-red-700 text-center px-4">
              Couldn't load contribution: {error}
            </div>
          ) : (
            <div className="flex h-28 items-center justify-center text-sm text-muted-foreground">
              Loading…
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export { getPaceStatus };
export type { PaceStatus };
